import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { Input } from 'reactstrap';
import { kebabCase } from 'lodash';
import classNames from 'classnames';
import { computeHeightInPixels, computeHeightInRows } from '../heightProps';
import { conformanceSelector, enabledSelector, labelSelector, newPathSelector, placeholderSelector, valueSelector } from '../selectors/index';
import entityComposition from '../../common/classes/entityComposition';
import Label from './label';
import ConformanceTooltip from './conformanceTooltip';
import onChangeFunc from '../commonActions/onChange';
import logValueFunc from '../commonActions/logValue';
import localChangeSelector from '../selectors/localChangeSelector';
const TextField = _ref => {
  let onChange = _ref.onChange,
    path = _ref.path,
    logValue = _ref.logValue,
    localChangeFunc = _ref.localChangeFunc,
    operationComplete = _ref.operationComplete,
    operationPending = _ref.operationPending,
    _ref$conformanceText = _ref.conformanceText,
    conformanceText = _ref$conformanceText === void 0 ? null : _ref$conformanceText,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? null : _ref$label,
    _ref$maxTextLength = _ref.maxTextLength,
    maxTextLength = _ref$maxTextLength === void 0 ? null : _ref$maxTextLength,
    _ref$onBlur = _ref.onBlur,
    onBlur = _ref$onBlur === void 0 ? () => {} : _ref$onBlur,
    _ref$onFocus = _ref.onFocus,
    onFocus = _ref$onFocus === void 0 ? () => {} : _ref$onFocus,
    _ref$placeholder = _ref.placeholder,
    placeholder = _ref$placeholder === void 0 ? '' : _ref$placeholder,
    _ref$testingIdentifie = _ref.testingIdentifier,
    testingIdentifier = _ref$testingIdentifie === void 0 ? 'default-id' : _ref$testingIdentifie,
    _ref$textColor = _ref.textColor,
    textColor = _ref$textColor === void 0 ? null : _ref$textColor,
    _ref$value = _ref.value,
    propsValue = _ref$value === void 0 ? '' : _ref$value,
    _ref$enabled = _ref.enabled,
    enabled = _ref$enabled === void 0 ? true : _ref$enabled,
    _ref$mdRows = _ref.mdRows,
    mdRows = _ref$mdRows === void 0 ? undefined : _ref$mdRows;
  const showConformance = !!conformanceText;
  const valueRef = useRef();
  const onBlurIfChanged = async function onBlurIfChanged() {
    let newValue = valueRef.current.value;
    if (propsValue !== newValue) {
      if (maxTextLength && newValue.length > maxTextLength) {
        newValue = newValue.substring(0, maxTextLength);
      }
      if (newValue === '') {
        newValue = null;
      }
      await onBlur(...arguments);
      await operationPending();
      const log = {
        value: newValue,
        displayValue: newValue,
        path
      };
      await onChange(localChangeFunc(log));
      await logValue(log);
      await operationComplete();
    }
  };
  const className = 't-ft';
  const id = "input-".concat(kebabCase(label));
  const testID = "".concat(className, "-").concat(kebabCase(testingIdentifier));
  const formClasses = classNames({
    ["".concat(className, "-").concat(id)]: true,
    ["".concat(testID, "-input")]: true,
    'tqc-control': true,
    'tqc-text-field': true
  });

  // sometimes value can change outside of
  // the input - via behaviors for example.
  useEffect(() => {
    var _valueRef$current;
    // resets value if new propsValue comes in changed.  PropsValue only gets updated when
    // an onBlur happens.
    if ((valueRef === null || valueRef === void 0 || (_valueRef$current = valueRef.current) === null || _valueRef$current === void 0 ? void 0 : _valueRef$current.value) !== propsValue) {
      // we updated props, we should update value here.
      if (valueRef.current !== undefined) {
        valueRef.current.value = propsValue;
      }
    }
  }, [propsValue]);
  const rows = computeHeightInRows({
    mdRows
  });
  return /*#__PURE__*/React.createElement("div", {
    className: "".concat(testID, "-form")
  }, label && /*#__PURE__*/React.createElement(Label, {
    value: label,
    style: {
      height: '32px'
    },
    color: textColor
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'relative'
    }
  }, /*#__PURE__*/React.createElement(Input, {
    id: id,
    "data-testid": id,
    type: rows > 1 ? 'textarea' : 'text',
    style: rows > 1 ? {
      height: computeHeightInPixels({
        mdRows
      })
    } : {},
    placeholder: placeholder,
    onBlur: onBlurIfChanged,
    onFocus: onFocus,
    className: formClasses,
    innerRef: valueRef,
    defaultValue: propsValue,
    disabled: !enabled,
    invalid: showConformance
  }), /*#__PURE__*/React.createElement(ConformanceTooltip, {
    "data-testid": "".concat(id, "-conformance"),
    errorText: conformanceText,
    target: id
  })));
};
TextField.propTypes = {
  path: PropTypes.string.isRequired,
  localChangeFunc: PropTypes.func.isRequired,
  conformanceText: PropTypes.string,
  maxTextLength: PropTypes.number,
  enabled: PropTypes.bool,
  label: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  logValue: PropTypes.func.isRequired,
  operationPending: PropTypes.func.isRequired,
  operationComplete: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  testingIdentifier: PropTypes.string,
  textColor: PropTypes.shape({
    red: PropTypes.number,
    green: PropTypes.number,
    blue: PropTypes.number
  }),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  mdRows: PropTypes.number
};
export { TextField as UncomposedTextField };
export default entityComposition(TextField, [], {
  conformanceText: conformanceSelector,
  enabled: enabledSelector,
  label: labelSelector,
  placeholder: placeholderSelector,
  value: valueSelector,
  path: newPathSelector,
  logValue: logValueFunc,
  onChange: onChangeFunc,
  localChangeFunc: localChangeSelector
});